import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  buildSelections,
  isAddable,
  reasonLabel,
  type LineSelection,
  type ReorderLine
} from "@/lib/reorder-diff";
import { convertToLocale } from "@/utils/money";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Minus,
  Plus,
  Search,
  ShoppingCart,
  Zap
} from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";

interface ReorderReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The resolved changed-line diff to review (already normalized). */
  lines: ReorderLine[];
  /** Supplier name for the header; omit for a cross-supplier saved list. */
  sellerName?: string | null;
  currencyCode: string;
  isConfirming: boolean;
  error?: string | null;
  /** The buyer's final pick + quantities, ready for the confirm mutation. */
  onConfirm: (selections: LineSelection[]) => void;
  /**
   * Same pick as `onConfirm`, but the buyer wants to skip the cart and go
   * straight to checkout. The "Quick Buy" button only renders when provided.
   */
  onQuickBuy?: (selections: LineSelection[]) => void;
  /**
   * Render per-line checkboxes + quantity editing. Defaults to `true`. Pass
   * `false` to keep a surface read-only / all-in (stock-tracking reorder,
   * which has no backend support for a partial add - see ADR-0004).
   */
  selectable?: boolean;
  /**
   * Dialog heading. Defaults to the reorder wording; a surface that is not a
   * reorder (a saved list) passes its own so the title fits the context.
   */
  title?: string;
}

/** A line's quantity clamps to at least 1 - removing a line is unchecking it. */
const MIN_QUANTITY = 1;

/**
 * Review-before-confirm step for a reorder. Renders the changed-line diff -
 * available lines (added at current price), repriced lines (old -> new), and
 * unavailable lines (skipped, with a manual "find alternative" link) - and lets
 * the buyer pick which addable lines to add and at what quantity before
 * confirming. Both F2 (reorder) and F3 (saved list) feed it the same
 * normalized `ReorderLine[]` and both get selection; `selectable={false}`
 * opts a surface out (see ADR-0004).
 */
export const ReorderReviewDialog = memo(function ReorderReviewDialog({
  open,
  onOpenChange,
  lines,
  sellerName,
  currencyCode,
  isConfirming,
  error,
  onConfirm,
  onQuickBuy,
  selectable = true,
  title = "Review your reorder"
}: ReorderReviewDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // A fresh preview means a fresh `lines` array - default to every addable
  // line checked, at its original quantity (today's all-in behavior, until
  // the buyer changes something).
  useEffect(() => {
    const nextSelected = new Set<string>();
    const nextQuantities: Record<string, number> = {};
    for (const line of lines) {
      if (isAddable(line) && line.variant_id) {
        nextSelected.add(line.variant_id);
        nextQuantities[line.variant_id] = line.quantity;
      }
    }
    setSelected(nextSelected);
    setQuantities(nextQuantities);
  }, [lines]);

  const addableLines = useMemo(() => lines.filter(isAddable), [lines]);
  const unavailableCount = lines.length - addableLines.length;
  const selectedCount = addableLines.filter(
    (line) => line.variant_id && selected.has(line.variant_id)
  ).length;
  const allSelected =
    addableLines.length > 0 && selectedCount === addableLines.length;

  const toggle = (variantId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) {
        next.delete(variantId);
      } else {
        next.add(variantId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(
      allSelected
        ? new Set()
        : new Set(
            addableLines
              .map((line) => line.variant_id)
              .filter((id): id is string => id !== null)
          )
    );
  };

  const setQuantity = (variantId: string, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [variantId]: Math.max(MIN_QUANTITY, quantity)
    }));
  };

  const fmt = (amount: number) =>
    convertToLocale({ amount, currency_code: currencyCode });

  const handleConfirm = () =>
    onConfirm(buildSelections(lines, selected, quantities));

  const handleQuickBuy = () =>
    onQuickBuy?.(buildSelections(lines, selected, quantities));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-border border-b p-6 pb-4">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {sellerName ? <>From {sellerName}. </> : null}
            {addableLines.length > 0
              ? `${addableLines.length} ${addableLines.length === 1 ? "item" : "items"} ready to add to your cart.`
              : "None of these items are available to reorder right now."}
            {unavailableCount > 0 && addableLines.length > 0
              ? ` ${unavailableCount} unavailable.`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {selectable && addableLines.length > 1 ? (
          <label className="border-border flex cursor-pointer items-center gap-2 border-b px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleAll}
              aria-label="Select all items"
            />
            Select all ({addableLines.length})
          </label>
        ) : null}

        <ul className="divide-border max-h-[50vh] divide-y overflow-y-auto px-6">
          {lines.map((line, index) => (
            <ReorderLineRow
              key={line.offer_id ?? line.variant_id ?? `line-${index}`}
              line={line}
              fmt={fmt}
              selectable={selectable}
              checked={Boolean(
                line.variant_id && selected.has(line.variant_id)
              )}
              quantity={
                (line.variant_id && quantities[line.variant_id]) ||
                line.quantity
              }
              onToggle={() => line.variant_id && toggle(line.variant_id)}
              onQuantityChange={(quantity) =>
                line.variant_id && setQuantity(line.variant_id, quantity)
              }
            />
          ))}
        </ul>

        {error && (
          <p
            role="alert"
            className="mx-6 mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          >
            {error}
          </p>
        )}

        <DialogFooter className="border-border gap-2 border-t p-6 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
          >
            Cancel
          </Button>
          {onQuickBuy ? (
            // Amber accent matches Quick Buy in the product detail dialog.
            <Button
              type="button"
              variant="outline"
              onClick={handleQuickBuy}
              disabled={selectedCount === 0 || isConfirming}
              className="border-amber-400"
            >
              <Zap className="mr-2 h-4 w-4 text-amber-500" aria-hidden="true" />
              Quick Buy
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || isConfirming}
          >
            <ShoppingCart className="mr-2 h-4 w-4" aria-hidden="true" />
            {isConfirming
              ? "Adding…"
              : `Add ${selectedCount} ${selectedCount === 1 ? "item" : "items"} to cart`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

const ReorderLineRow = memo(function ReorderLineRow({
  line,
  fmt,
  selectable,
  checked,
  quantity,
  onToggle,
  onQuantityChange
}: {
  line: ReorderLine;
  fmt: (amount: number) => string;
  selectable: boolean;
  checked: boolean;
  quantity: number;
  onToggle: () => void;
  onQuantityChange: (quantity: number) => void;
}) {
  const unavailable = line.status === "unavailable";
  const addable = isAddable(line) && line.variant_id !== null;
  const showControls = selectable && addable;

  return (
    <li
      className={cn(
        "flex gap-3 py-3",
        showControls && !checked && "opacity-50"
      )}
    >
      {showControls ? (
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          aria-label={`Include ${line.title}`}
          className="mt-1 flex-shrink-0"
        />
      ) : null}

      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-neutral-800">
        <ProductThumbnailImage
          src={line.thumbnail ?? null}
          alt=""
          className={cn(
            "h-full w-full object-cover",
            unavailable && "opacity-50 grayscale"
          )}
          fallback={null}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <TruncateTooltip
            as="p"
            className={cn(
              "text-sm font-medium",
              unavailable
                ? "text-gray-400 line-through dark:text-gray-500"
                : "text-gray-900 dark:text-white"
            )}
          >
            {line.title}
          </TruncateTooltip>
          <LinePrice line={line} quantity={quantity} fmt={fmt} />
        </div>

        {showControls ? (
          <div className="border-border mt-1.5 inline-flex w-fit items-center rounded-md border">
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-neutral-800"
              onClick={() => onQuantityChange(quantity - 1)}
              disabled={!checked || quantity <= MIN_QUANTITY}
              aria-label={`Decrease quantity of ${line.title}`}
            >
              <Minus className="h-3 w-3" aria-hidden="true" />
            </button>
            <span className="w-7 text-center text-xs tabular-nums">
              {quantity}
            </span>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-neutral-800"
              onClick={() => onQuantityChange(quantity + 1)}
              disabled={!checked}
              aria-label={`Increase quantity of ${line.title}`}
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <p className="mt-0.5 text-xs text-gray-500">Qty: {line.quantity}</p>
        )}

        {line.seller?.name ? (
          // Per-line supplier - the variant-keyed stock reorder resolves each
          // line to a seller and can span suppliers, so "from <supplier>" lives
          // on the row. Order / saved-list reorders carry one seller in the
          // header instead and set no per-line seller, so this stays hidden.
          <TruncateTooltip
            as="p"
            className="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
          >
            from {line.seller.name}
          </TruncateTooltip>
        ) : null}

        {line.status === "repriced" && (
          <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Price changed since your last order
          </p>
        )}

        {unavailable && (
          <div className="mt-1 flex items-center gap-2">
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-neutral-800 dark:text-gray-400">
              {reasonLabel(line.reason)}
            </span>
            <Link
              to="/search"
              search={{ q: line.title }}
              className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:underline dark:text-purple-400"
            >
              <Search className="h-3 w-3" aria-hidden="true" />
              Find alternative
            </Link>
          </div>
        )}
      </div>
    </li>
  );
});

function LinePrice({
  line,
  quantity,
  fmt
}: {
  line: ReorderLine;
  quantity: number;
  fmt: (amount: number) => string;
}) {
  const edited = quantity !== line.quantity;

  if (line.status === "repriced") {
    return (
      <span className="flex-shrink-0 text-right text-sm">
        <span className="mr-1 text-gray-400 line-through">
          {fmt(line.old_price ?? 0)}
        </span>
        <span className="font-semibold text-gray-900 dark:text-white">
          {fmt(line.new_price ?? 0)}
        </span>
        {edited ? (
          <span className="block text-xs text-gray-500">
            × {quantity} = {fmt((line.new_price ?? 0) * quantity)}
          </span>
        ) : null}
      </span>
    );
  }

  if (line.status === "available") {
    return (
      <span className="flex-shrink-0 text-right text-sm">
        <span className="font-semibold text-gray-900 dark:text-white">
          {fmt(line.unit_price ?? 0)}
        </span>
        {edited ? (
          <span className="block text-xs text-gray-500">
            × {quantity} = {fmt((line.unit_price ?? 0) * quantity)}
          </span>
        ) : null}
      </span>
    );
  }

  return null;
}
